<template>
  <div class="substep-add-a-holiday">
    <div class="substep-header">
      <h3 data-testId="step-title">Add a holiday</h3>
      <p>
        Add paid holidays for your team. Members will be paid for 8 hours for the specific day. You can always add or
        create custom holidays later on.
      </p>
    </div>
    <div class="substep-body">
      <div class="holidays">
        <div
          v-for="option in holidays"
          :key="option.name"
          class="option"
          :class="{ selected: isSelected(option.name) }"
          @click="toggleOption(option.name)"
        >
          <input
            v-model="selectedOptions"
            type="checkbox"
            :value="option.value"
          />
          <i
            v-if="!isSelected(option.name)"
            class="hi hi-plus hi-12"
          ></i>
          {{ `${formatName(option.name)} (${formatDate(option.date)})` }}
          <i
            v-if="isSelected(option.name) && !option.id"
            class="hi hi-close hi-12"
          ></i>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import GettingStartedMixin from '~/mixins/GettingStarted/GettingStartedMixin';
import { HolidaysResource } from '~/resources';

export default {
  name: 'AddAHoliday',
  mixins: [GettingStartedMixin],
  inject: {
    wizardBus: { default: () => ({ on: () => {} }) },
  },
  props: {
    holidays: {
      type: Array,
      default: () => [],
      validator: (value) => {
        const requiredAttributes = ['name', 'date'];
        const hasRequiredAttributes = (holiday) => requiredAttributes.every((attr) => Object.keys(holiday).includes(attr));

        return value.every((holiday) => hasRequiredAttributes(holiday));
      },
    },
    organization: {
      type: Object,
      required: true,
      validator: (value) => {
        const valueKeys = Object.keys(value);

        return valueKeys.includes('id');
      },
    },
  },
  data() {
    return {
      selectedOptions: [],
    };
  },
  watch: {
    holidays: {
      immediate: true,
      handler(newHolidays) {
        this.initializeSelectedOptions(newHolidays);
      },
    },
  },
  created() {
    this.wizardBus.on('userClickedContinue', this.handleFinishedSubstep);
  },
  beforeMount() {
    this.$emit('completeSubstep');
  },
  methods: {
    formatName(holidayName) {
      const emojis = {
        'Easter Monday': '🥚',
        'Christmas Eve': '🎄',
        'Christmas Day': '🎁',
        "New Year's Eve": '🥳',
        "New Year's Day": '🎉',
        Thanksgiving: '🦃',
      };

      return `${emojis[holidayName] ? emojis[holidayName] : ''} ${holidayName}`;
    },
    formatDate(date) {
      return moment(date).format('MMM Do');
    },
    initializeSelectedOptions(holidays) {
      this.selectedOptions = this.holidays.filter((option) => option.selected).map((option) => option.name);
    },
    isSelected(holidayName) {
      return this.selectedOptions.includes(holidayName);
    },
    toggleOption(holidayName) {
      const index = this.selectedOptions.indexOf(holidayName);
      const holiday = this.holidays.find((option) => option.name === holidayName);

      if (holiday && holiday.id) {
        return;
      }

      if (index === -1) {
        this.selectedOptions.push(holidayName);
        if (holiday) {
          holiday.selected = true;
        }
      } else {
        this.selectedOptions.splice(index, 1);
        if (holiday) {
          holiday.selected = false;
        }
      }
    },
    async handleFinishedSubstep() {
      await this.createHolidays();
      this.$emit('finishSubstep');
    },
    async createHolidays() {
      const params = {
        holidays: this.selectedOptions
          .map((option) => {
            const holiday = this.holidays.find((holiday) => holiday.name === option);

            if (holiday.id) {
              return {};
            }

            return {
              amount_used: 28800,
              auto_add_new_users: true,
              name: holiday.name,
              date: holiday.date,
              symbolic_type: holiday.symbolic_type,
              recurring: true,
            };
          })
          .filter((holiday) => holiday.name),
      };

      if (!params.holidays.length) {
        return;
      }

      try {
        const response = await HolidaysResource.bulkCreate({ organizationId: this.organization.id }, params);

        if (response.success) {
          this.$appMessage('Holidays created successfully');
        } else if (response.errors) {
          this.$appMessage('Something went wrong. Please try again.');
        }
      } catch (error) {
        if (![401, 403].includes(error.status)) {
          this.$appMessage('Unhandled error. Please try again.');
        }
      }
    },
  },
};
</script>
